/**
 * Stripe Service (frontend React)
 *
 * Appelle les Firebase Cloud Functions pour :
 *   - Créer une session Stripe Checkout
 *   - Ouvrir le portail de gestion Stripe
 *
 * Cloud Functions déployées dans functions/index.js :
 *   createCheckoutSession  → europe-west1-myadhdtasksnow.cloudfunctions.net
 *   createPortalSession    → europe-west1-myadhdtasksnow.cloudfunctions.net
 *   stripeWebhook          → reçoit les webhooks (configuré dans Stripe Dashboard)
 *
 * Variables .env requises (côté React) :
 *   REACT_APP_STRIPE_PUBLIC_KEY    — pk_live_... ou pk_test_...
 *   REACT_APP_STRIPE_PRO_MONTHLY   — price_... (Price ID mensuel)
 *   REACT_APP_STRIPE_PRO_ANNUAL    — price_... (Price ID annuel)
 *
 * Variables secrets Firebase (à configurer via CLI) :
 *   firebase functions:secrets:set STRIPE_SECRET_KEY
 *   firebase functions:secrets:set STRIPE_WEBHOOK_SECRET
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import app from './firebase';

// Lazy-init Firebase Functions (region europe-west1)
let functionsInstance = null;
function getFunctionsInstance() {
  if (!functionsInstance) {
    functionsInstance = getFunctions(app, 'europe-west1');
  }
  return functionsInstance;
}

/**
 * Crée une session Stripe Checkout via Cloud Function et redirige l'utilisateur
 * @param {string} priceId   — Price ID Stripe (REACT_APP_STRIPE_PRO_MONTHLY ou _ANNUAL)
 * @param {string} userId    — UID Firebase (pour lier le paiement à l'utilisateur)
 * @param {string} email     — Email (pré-rempli dans le formulaire Stripe)
 */
export async function createCheckoutSession(priceId, userId, email) {
  if (!priceId) {
    console.error('[Stripe] Price ID manquant — vérifiez REACT_APP_STRIPE_PRO_MONTHLY / _ANNUAL dans .env');
    alert('Configuration incomplète : Price ID Stripe manquant.\nAjoutez REACT_APP_STRIPE_PRO_MONTHLY et REACT_APP_STRIPE_PRO_ANNUAL dans votre fichier .env');
    return;
  }

  if (!userId) {
    alert('Vous devez être connecté pour vous abonner.');
    return;
  }

  try {
    const functions = getFunctionsInstance();
    const createSession = httpsCallable(functions, 'createCheckoutSession');

    const result = await createSession({
      priceId,
      successUrl: `${window.location.origin}?success=true`,
      cancelUrl: `${window.location.origin}?cancelled=true`,
    });

    const { url } = result.data;
    if (url) {
      window.location.assign(url);
    } else {
      throw new Error('URL de session manquante dans la réponse');
    }
  } catch (err) {
    console.error('[Stripe] createCheckoutSession error:', err);

    // Message d'erreur user-friendly selon le type d'erreur
    if (err.code === 'functions/unauthenticated') {
      alert('Session expirée. Veuillez vous reconnecter.');
    } else if (err.code === 'functions/not-found' || err.message?.includes('ECONNREFUSED')) {
      // Functions pas encore déployées — afficher instructions
      alert(
        'Les Cloud Functions ne sont pas encore déployées.\n\n' +
        'Pour activer les paiements :\n' +
        '1. Activez le plan Blaze Firebase (firebase.google.com)\n' +
        '2. Ajoutez vos secrets : firebase functions:secrets:set STRIPE_SECRET_KEY\n' +
        '3. Déployez : firebase deploy --only functions\n\n' +
        'En attendant, retrouvez les instructions complètes dans le rapport :\n' +
        'myadhdtasksnow.web.app/rapport-commercialisation.html'
      );
    } else {
      alert(`Erreur de paiement : ${err.message}`);
    }
  }
}

/**
 * Ouvre le portail Stripe pour gérer l'abonnement (annulation, CB, factures)
 * @param {string} userId — UID Firebase
 */
export async function openCustomerPortal(userId) {
  if (!userId) {
    alert('Vous devez être connecté.');
    return;
  }

  try {
    const functions = getFunctionsInstance();
    const createPortal = httpsCallable(functions, 'createPortalSession');
    const result = await createPortal({});
    const { url } = result.data;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  } catch (err) {
    console.error('[Stripe] openCustomerPortal error:', err);
    if (err.code === 'functions/not-found') {
      alert('Portail de gestion non disponible. Les Cloud Functions ne sont pas encore déployées.');
    } else {
      alert(`Erreur : ${err.message}`);
    }
  }
}

/**
 * Vérifie si le retour depuis Stripe est un succès ou une annulation
 * Lit les query params ?success=true ou ?cancelled=true
 * @returns {'success' | 'cancelled' | null}
 */
export function checkStripeReturn() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('success') === 'true') {
    window.history.replaceState({}, '', window.location.pathname);
    return 'success';
  }
  if (params.get('cancelled') === 'true') {
    window.history.replaceState({}, '', window.location.pathname);
    return 'cancelled';
  }
  return null;
}

/**
 * URL du webhook à enregistrer dans le Dashboard Stripe :
 * https://europe-west1-myadhdtasksnow.cloudfunctions.net/stripeWebhook
 *
 * Événements à activer dans Stripe :
 *   - checkout.session.completed
 *   - customer.subscription.created
 *   - customer.subscription.updated
 *   - customer.subscription.deleted
 *   - invoice.payment_succeeded
 *   - invoice.payment_failed
 */
export const WEBHOOK_URL = 'https://europe-west1-myadhdtasksnow.cloudfunctions.net/stripeWebhook';
